import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Drawer, Steps, message } from "antd";

import { controller as rootController } from "../../controller";
import { savePolicyDrafts } from "../TreatmentPlans/component/shared/PolicyCompletionStep";
import PatientPolicyInfoFormModal from "../TreatmentPlans/component/PatientPolicyInfoFormModal";
import { useEnsurePolicies } from "../TreatmentPlans/component/useEnsurePolicies";

import RequestTypeStep from "./RequestTypeStep";
import PatientStep from "./PatientStep";
import PolicySelectStep from "./PolicySelectStep";
import {
  buildPreviewSavePayload, claimFormValues, isPendingId, pendingSource, toPendingCards,
} from "./pendingPolicies";
import { choicePayload, parseCarrierConflict } from "./carrierChoice";
import { describeApiError } from "../TreatmentPlans/lib/apiError";
import EstimateChecklist from "./EstimateChecklist";
import OfficeCdaPrompt from "./OfficeCdaPrompt";
import {
  fetchEstimateDefaults,
  matchClaimFormPolicies,
  saveClaimFormPolicy,
  serializeEstimates,
} from "../TreatmentPlans/component/shared/estimateClaimsApi";
import { controller } from "./controller";
import { needsSubmissions, requestTypeMeta } from "./requestTypes";
import { composerSteps, nextStepKey, previousStepKey } from "./composerSteps";
import {
  claimFormCards,
  claimFormPreselection,
  claimFormSuggestions,
  describeClaimForm,
  estimatesFromClaimForm,
  exactMatch,
} from "./claimForm";

/** The wizard's office — the one the page is on (the patient search and
 *  add-patient use the same). */
function selectedOfficeId() {
  return localStorage.getItem("selectedOffice");
}

/**
 * The composer: request → patient → policies → (estimates / claims), in a
 * right-side Drawer. The steps themselves are decided in composerSteps.js.
 *
 * The Drawer is deliberately the only high-energy surface on the page. Everything
 * else is a resting table; this is the one place the user is *doing* something,
 * so it gets the full width of their attention rather than a modal sat on top of
 * the list they were reading.
 *
 * The patient can come from an uploaded claim form ("Upload as File"). Then the
 * form also names their plan(s) — offered on the Policies step, as the policy
 * the patient already has or as a card to approve — and IS the submission for an
 * estimate or a claim follow-up, so the step that would ask for it is skipped.
 *
 * Policies are topped up from the PMS as soon as a patient is chosen, so the
 * review asks only for what we genuinely could not fetch.
 */
export default function RequestComposerDrawer({
  open,
  onClose,
  onSubmitted,
  // Seeding, for the entry points that already know who they are asking about —
  // the insurance modal, the treatment-plans row, the plan-details card, the
  // claim follow-ups pane. All optional, all defaulting to the Insurance
  // Verification page's behaviour, which passes none of them. A seeded patient
  // or type drops the step that would ask for it (composerSteps).
  initialPatient = null,
  initialPolicyIds = null,
  initialRequestType = null,
  // Set when launched over another surface. The insurance modal sits at antd's
  // auto ~1000 — InsuranceMasterModal deliberately sets no zIndex of its own.
  zIndex,
}) {
  const seededPatient = !!initialPatient;
  const seededType = !!initialRequestType;
  // Nothing is chosen until the user picks a card: a pre-selected Breakdown was
  // easy to sail past. An entry point that already knows the type seeds it.
  const [requestType, setRequestType] = useState(initialRequestType || null);
  // "find" | "upload" — which tile on the Patient step. Null until picked.
  const [patientSource, setPatientSource] = useState(null);
  // The read of an uploaded claim form (estimateClaimsApi.readClaimForm).
  const [claimForm, setClaimForm] = useState(null);
  // Its plans set against the chosen patient (matchClaimFormPolicies).
  const [claimFormPolicies, setClaimFormPolicies] = useState(null);

  // A file nothing could be read from isn't the submission: the request asks
  // for it on its own step, exactly as when the patient was found by hand.
  const readNothing = patientSource === "upload" && !!claimForm && !!claimForm.extraction_empty;
  const steps = composerSteps({
    seededType, seededPatient, requestType, patientSource: readNothing ? "find" : patientSource,
  });
  const [stepKey, setStepKey] = useState(steps[0].key);

  const [patient, setPatient] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [urgent, setUrgent] = useState(false);
  const [drafts, setDrafts] = useState({});
  // { [policyId]: true } — set only by an explicit Approve, which is also what
  // writes the policy. Cleared the moment a field is edited again.
  const [approved, setApproved] = useState({});
  // policyId -> [estimate, ...]. Only meaningful for a submissions step.
  const [estimates, setEstimates] = useState({});
  // Which policies' estimates the user has confirmed: completeness alone is not
  // the gate, saying so is.
  const [savedEstimates, setSavedEstimates] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [addingPolicy, setAddingPolicy] = useState(false);
  // A policy the server said is incomplete, to open on the Policies step.
  const [focusPolicyId, setFocusPolicyId] = useState(null);
  // Suggestions for the estimate editor's provider field. Suggestions only —
  // the field stays free text because a claim form may name someone who isn't
  // on file, and refusing that would block a legitimate request.
  const [providerOptions, setProviderOptions] = useState([]);
  // The office's saved CDA/iTrans number, which pre-fills every estimate row.
  const [cdaItrans, setCdaItrans] = useState("");

  const patientId = patient && patient.id;
  // The uploaded form is this request's submission — only when the patient came
  // from it (a seeded launch has no Patient step, so never), and only when
  // something was read from it.
  const usingClaimForm = patientSource === "upload" && !!claimForm && !readNothing;

  // Office-level pre-fills, keyed by patient so the office is derived server-side
  // rather than taken from the header.
  useEffect(
    function () {
      if (!patientId) { setCdaItrans(""); return undefined; }
      let cancelled = false;
      (async function () {
        const defaults = await fetchEstimateDefaults(patientId);
        if (!cancelled) setCdaItrans((defaults && defaults.cda_itrans_number) || "");
      })();
      return function () { cancelled = true; };
    },
    [patientId],
  );

  const refreshPolicies = useCallback(
    async function refreshPoliciesInner() {
      if (!patientId) return [];
      try {
        const res = await rootController.getPatientPolicies(patientId);
        if (res.status < 250 && Array.isArray(res.json)) {
          const active = res.json.filter(function (p) { return p.is_active !== false; });
          setPolicies(active);
          return active;
        }
      } catch (e) {
        console.error(e);
      }
      return [];
    },
    [patientId],
  );

  // Shared with the board: auto-fetches a policy-less patient's insurance from
  // the PMS, deduped per patient. `quiet`: the policy panel states the outcome
  // itself. `deferReview`: an unmatched carrier comes back to us as a card
  // instead of opening a modal over the panel.
  const {
    ensurePolicies, refreshFromPms, isFetchingPolicies,
    pendingReview, clearPendingReview, refetchPreview, pmsPreviews,
  } = useEnsurePolicies(patientId, null, refreshPolicies, { quiet: true, deferReview: true });

  // Pending cards that have since been approved into real rows. The PMS's and
  // the form's payloads are left untouched; this is our record of what we have
  // already done with them.
  const [resolvedPending, setResolvedPending] = useState({});

  // Cards for policies we could not save yet: the PMS's with an unmatched
  // carrier, and the form's plans the patient doesn't have.
  const pendingCards = useMemo(function () {
    const fromPms = toPendingCards(pendingReview);
    const fromForm = claimFormPolicies ? claimFormCards(claimFormPolicies, policies) : [];
    return fromPms.concat(fromForm).filter(function (card) { return !resolvedPending[card.id]; });
  }, [pendingReview, claimFormPolicies, policies, resolvedPending]);

  // One list for the picker. A pending card is selectable like any other; it
  // becomes a real policy when it is approved.
  const allPolicies = useMemo(function () {
    return policies.concat(pendingCards);
  }, [policies, pendingCards]);

  // Suggestions beside each saved policy's fields: the PMS's, and the form's
  // for a plan the patient already has — the form wins, it's what was just sent.
  const reviewPreviews = useMemo(function () {
    return Object.assign(
      {}, pmsPreviews,
      claimFormPolicies ? claimFormSuggestions(claimFormPolicies, policies) : {},
    );
  }, [pmsPreviews, claimFormPolicies, policies]);

  useEffect(
    function () {
      if (!open) return undefined;
      let cancelled = false;
      (async function () {
        try {
          const res = await rootController.getProviderList();
          const rows = (res && (res.results || res.data || res)) || [];
          if (!cancelled && Array.isArray(rows)) {
            setProviderOptions(
              rows
                .map(function (p) {
                  const name = [p.firstname, p.lastname].filter(Boolean).join(" ") || p.name;
                  return name ? { value: name } : null;
                })
                .filter(Boolean),
            );
          }
        } catch (e) {
          // Suggestions are a convenience; the field works without them.
          console.error(e);
        }
      })();
      return function () { cancelled = true; };
    },
    [open],
  );

  // Reset everything on each open so a previous draft can never leak into a new
  // request (this Drawer is not destroyed on close — the page keeps it mounted).
  useEffect(
    function () {
      if (!open) return;
      setRequestType(initialRequestType || null);
      setPatientSource(null);
      setClaimForm(null);
      setClaimFormPolicies(null);
      setStepKey(composerSteps({ seededType, seededPatient })[0].key);
      setPatient(initialPatient || null);
      setPolicies([]);
      setSelectedIds(initialPolicyIds ? initialPolicyIds.slice() : []);
      setUrgent(false);
      setDrafts({});
      setApproved({});
      setEstimates({});
      setSavedEstimates({});
      setAddingPolicy(false);
      setFocusPolicyId(null);
      clearPendingReview();
      setResolvedPending({});
    },
    [open, clearPendingReview],
  );

  // A step can stop existing under the user — the submissions step goes when an
  // uploaded form takes its place — so land on the nearest one that remains.
  useEffect(
    function () {
      if (!steps.some(function (s) { return s.key === stepKey; })) {
        setStepKey(steps[steps.length - 1].key);
      }
    },
    [steps.map(function (s) { return s.key; }).join("|"), stepKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  /**
   * A policy was just entered by hand. Reload, then pre-select whatever is new so
   * the user carries straight on instead of having to find and tick it.
   *
   * This is the ONLY route forward when the office has no working PMS connection
   * — auto-fetch-and-save answers 502 there, which useEnsurePolicies degrades to a
   * soft warning.
   */
  const handlePolicyAdded = useCallback(
    async function handlePolicyAddedInner() {
      const known = new Set(policies.map(function (p) { return p.id; }));
      const fresh = await refreshPolicies();
      const added = fresh.filter(function (p) { return !known.has(p.id); });
      if (added.length) {
        setSelectedIds(function (prev) {
          const next = prev.slice();
          added.forEach(function (p) { if (next.indexOf(p.id) === -1) next.push(p.id); });
          return next;
        });
      }
      setAddingPolicy(false);
    },
    [policies, refreshPolicies],
  );

  // On choosing a patient: pull what we have, fetch from the PMS if there is
  // nothing, then top up whatever we do have — and, when they came from an
  // uploaded form, set its plans against what they now hold.
  useEffect(
    function () {
      if (!patientId) return undefined;
      let cancelled = false;
      const form = usingClaimForm ? claimForm : null;
      (async function () {
        setLoadingPolicies(true);
        try {
          const current = await refreshPolicies();
          if (cancelled) return;
          if (!current.length) {
            await ensurePolicies(current);
          } else {
            await refreshFromPms();
          }
          if (cancelled) return;
          const latest = await refreshPolicies();
          if (cancelled || !form || !(form.policies || []).length) return;
          const res = await matchClaimFormPolicies(patientId, form.policies);
          if (cancelled) return;
          // Unreadable answer: the form's plans as they were read, all as cards.
          const annotated = res && res.status < 250 && res.json && Array.isArray(res.json.policies)
            ? res.json.policies
            : form.policies;
          setClaimFormPolicies(annotated);
          const preselect = claimFormPreselection(annotated, latest);
          if (preselect != null) {
            setSelectedIds(function (prev) {
              return prev.indexOf(preselect) === -1 ? prev.concat([preselect]) : prev;
            });
          }
        } finally {
          if (!cancelled) setLoadingPolicies(false);
        }
      })();
      return function () { cancelled = true; };
    },
    [patientId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const selectedPolicies = allPolicies.filter(function (p) {
    return selectedIds.indexOf(p.id) !== -1;
  });
  // Not "is it complete" but "did the user approve it" — a stronger claim, since
  // approving is what saves the details to the policy.
  const detailsReady =
    selectedPolicies.length > 0 &&
    selectedPolicies.every(function (p) { return !!approved[p.id]; });
  const noPendingSelected = !selectedIds.some(isPendingId);
  const policiesReady = detailsReady && noPendingSelected && !addingPolicy;

  const meta = requestTypeMeta(requestType);
  const hasSubmissionsStep = steps.some(function (s) { return s.key === "submissions"; });
  // A submissions step must NAME what we are chasing. The server enforces this
  // too; blocking here just avoids a round trip that comes back as a validation
  // error. With an uploaded form there is no step: the form is the submission.
  const estimatesReady =
    !hasSubmissionsStep ||
    selectedPolicies.every(function (p) { return !!savedEstimates[p.id]; });
  const canSend = policiesReady && estimatesReady;

  const estimatesOutstanding = selectedPolicies.filter(function (p) {
    return !savedEstimates[p.id];
  }).length;

  /** Estimates changed: what was saved no longer matches, so it must be re-saved. */
  function handleEstimatesChange(policyId, next) {
    setEstimates(function (prev) {
      return Object.assign({}, prev, { [policyId]: next });
    });
    setSavedEstimates(function (prev) {
      if (!prev[policyId]) return prev;
      const out = Object.assign({}, prev);
      delete out[policyId];
      return out;
    });
  }

  /** A field changed: the saved policy no longer matches, so it must be re-approved. */
  function handleDraftChange(policyId, field, value) {
    setDrafts(function (prev) {
      return Object.assign({}, prev, {
        [policyId]: Object.assign({}, prev[policyId], { [field]: value }),
      });
    });
    setApproved(function (prev) {
      if (!prev[policyId]) return prev;
      const next = Object.assign({}, prev);
      delete next[policyId];
      return next;
    });
  }

  /**
   * Turn a pending card into a real policy, now that its carrier is known.
   *
   * A plan read off a claim form is saved as a person-approved policy (the
   * add-policy form's own save, carrier locked). A PMS card goes through its
   * preview: the token behind it expires after 15 minutes and a wizard session
   * can run longer, so a `preview_expired` answer is a normal outcome — mint a
   * fresh preview and try once more.
   *
   * Returns the saved policy row, or {conflict} when a new carrier is one we
   * already have, or {error} with the backend's reason.
   */
  const savePendingPolicy = useCallback(
    async function savePendingPolicyInner(card, choice, draft) {
      const preview = card.__preview;
      let res;

      if (pendingSource(card) === "claim_form") {
        const values = claimFormValues(preview);
        Object.keys(draft || {}).forEach(function (field) {
          const value = draft[field];
          if (value !== undefined && value !== null && String(value).trim() !== "") values[field] = value;
        });
        res = await saveClaimFormPolicy({
          patientId: patientId,
          preview: preview,
          carrierChoice: choicePayload(choice),
          values: values,
        });
      } else {
        const attempt = async function (token) {
          if (!token) return null;
          return rootController.savePatientPoliciesFromSikkaPreview({
            preview_token: token,
            policies: [buildPreviewSavePayload(preview, choice, draft)],
          });
        };
        res = await attempt(pendingReview && pendingReview.preview_token);
        if (res && res.status >= 250 && res.json && res.json.error_type === "preview_expired") {
          const fresh = await refetchPreview();
          res = await attempt(fresh && fresh.preview_token);
        }
      }

      if (!res || !res.status) return null;
      if (res.status >= 250) {
        const conflict = parseCarrierConflict(res.json);
        if (conflict) return { conflict: conflict };
        return { error: describeApiError(res, "") };
      }
      if (pendingSource(card) === "claim_form") return res.json || null;
      // The PMS save answers with the patient's WHOLE policy list; ours is the
      // one for this ordinal.
      const saved = (Array.isArray(res.json) ? res.json : []).find(function (p) {
        return p.insurance_type === preview.insurance_type;
      });
      return saved || null;
    },
    [patientId, pendingReview, refetchPreview],
  );

  /**
   * A policy was approved — i.e. its details are now on the server. Fold them into
   * our copy and drop the draft, so `savePolicyDrafts` at submit finds nothing to
   * write and can't re-PATCH values that are already saved.
   */
  function handlePolicyApproved(policyId, savedValues, savedPolicy) {
    // A pending card had no row behind it. Approving created one, so retire the
    // synthetic id in favour of the real one — in the policy list, in the
    // selection that becomes `policy_ids`, and in the approved map.
    if (isPendingId(policyId) && savedPolicy && savedPolicy.id) {
      setPolicies(function (prev) {
        return prev.some(function (p) { return p.id === savedPolicy.id; })
          ? prev.map(function (p) { return p.id === savedPolicy.id ? savedPolicy : p; })
          : prev.concat([savedPolicy]);
      });
      setSelectedIds(function (prev) {
        const next = prev.map(function (id) { return id === policyId ? savedPolicy.id : id; });
        return next.filter(function (id, i) { return next.indexOf(id) === i; });
      });
      setDrafts(function (prev) {
        const next = Object.assign({}, prev);
        delete next[policyId];
        return next;
      });
      setApproved(function (prev) {
        const next = Object.assign({}, prev);
        delete next[policyId];
        next[savedPolicy.id] = true;
        return next;
      });
      // Drop it from the pending set so the card list stops offering it.
      setResolvedPending(function (prev) {
        return Object.assign({}, prev, { [policyId]: true });
      });
      return;
    }

    setPolicies(function (prev) {
      return prev.map(function (p) {
        return p.id === policyId ? Object.assign({}, p, savedValues) : p;
      });
    });
    setDrafts(function (prev) {
      const next = Object.assign({}, prev);
      delete next[policyId];
      return next;
    });
    setApproved(function (prev) {
      return Object.assign({}, prev, { [policyId]: true });
    });
  }

  /**
   * The platform's policy form, rendered bare so it lives INSIDE the Policies step
   * instead of throwing a modal over the drawer mid-wizard.
   */
  function renderAddPolicyForm() {
    if (!patientId) return null;
    return (
      <PatientPolicyInfoFormModal
        embedded
        showPatientInsuranceFormModal={addingPolicy}
        setShowPatientInsuranceFormModal={setAddingPolicy}
        initialMode="add"
        patientID={patientId}
        patient={patient}
        // No-ops on purpose: in the treatment-plans row this callback dispatches
        // an eligibility agent on save, which must NOT happen from the composer —
        // the user submits the request explicitly at the end.
        editPatientInsuranceData={false}
        setEditPatientInsuranceData={function () {}}
        checkPatientInsuranceEligibility={function () {}}
        setCancelProcess={function () {}}
        handleReadData2={handlePolicyAdded}
      />
    );
  }

  function handleSelectPatient(p) {
    setPatient(p);
    setSelectedIds([]);
    setDrafts({});
    setApproved({});
    setEstimates({});
    setSavedEstimates({});
    // Switching patients must not carry a half-open policy form over to someone
    // else, nor the previous patient's unmatched carriers or form plans.
    setAddingPolicy(false);
    setFocusPolicyId(null);
    clearPendingReview();
    setClaimFormPolicies(null);
    setResolvedPending({});
  }

  /** Switching tiles starts the Patient step over: a form uploaded on one tile
   *  is not this request's submission once the other is chosen. */
  function handleSource(key) {
    if (key === patientSource) return;
    setPatientSource(key);
    setClaimForm(null);
    handleSelectPatient(null);
  }

  function handleClaimFormRead(read) {
    setClaimForm(read);
    // One exact match (both names and the date of birth) is selected for them;
    // the step says so and offers "Not them?".
    handleSelectPatient(exactMatch(read && read.candidates));
  }

  function handleClaimFormReplace() {
    setClaimForm(null);
    handleSelectPatient(null);
  }

  function toggle(id) {
    setSelectedIds(function (prev) {
      return prev.indexOf(id) === -1
        ? prev.concat([id])
        : prev.filter(function (x) { return x !== id; });
    });
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      // Approving each policy already wrote it and cleared its draft, so this is
      // normally a no-op. Kept as the backstop for anything that could leave an
      // unsaved edit behind.
      const saved = await savePolicyDrafts(selectedPolicies, drafts);
      if (!saved) {
        message.error("Couldn't save the policy details. Please try again.");
        return;
      }
      let submissions;
      if (needsSubmissions(requestType)) {
        // Every submission-bearing type sends its rows — from the uploaded form,
        // or from the step that collected them.
        submissions = usingClaimForm
          ? estimatesFromClaimForm(claimForm, selectedIds)
          : serializeEstimates(estimates, selectedIds);
      }
      const res = await controller.createRequest({
        patientId: patient.id,
        policyIds: selectedIds,
        requestType,
        urgency: urgent ? "urgent" : "normal",
        estimates: submissions,
      });
      if (res && res.status >= 200 && res.status < 300) {
        message.success(meta.successToast);
        onSubmitted();
        onClose();
        return;
      }
      // The server enforces the same completeness rule the UI does; if it still
      // rejects, something changed underneath us — send the user back to fix it
      // rather than failing with a generic error.
      const body = (res && res.json) || {};
      if (body.error_type === "incomplete_policies") {
        await refreshPolicies();
        const first = (body.policies || [])[0];
        setApproved(function (prev) {
          const next = Object.assign({}, prev);
          (body.policies || []).forEach(function (p) { delete next[p.policy_id]; });
          return next;
        });
        setFocusPolicyId(first ? first.policy_id : null);
        setStepKey("policies");
        message.warning("Some details are still missing — please complete them below.");
        return;
      }
      message.error(body.detail || "Couldn't send the request.");
    } catch (e) {
      console.error(e);
      message.error("Couldn't send the request.");
    } finally {
      setSubmitting(false);
    }
  }

  const back = previousStepKey(steps, stepKey);
  const next = nextStepKey(steps, stepKey);

  function backButton() {
    // The first step has nowhere to go back to: Cancel is the way out.
    return back
      ? <Button onClick={function () { setStepKey(back); }}>Back</Button>
      : <Button onClick={onClose}>Cancel</Button>;
  }

  function sendButton() {
    return (
      <Button type="primary" loading={submitting} disabled={!canSend} onClick={handleSubmit}>
        {urgent ? "Send request (urgent)" : "Send request"}
      </Button>
    );
  }

  function footer() {
    if (stepKey === "type") {
      return (
        <>
          {backButton()}
          <Button type="primary" disabled={!requestType} onClick={function () { setStepKey(next); }}>
            Continue
          </Button>
        </>
      );
    }
    if (stepKey === "patient") {
      return (
        <>
          {backButton()}
          <Button type="primary" disabled={!patient} onClick={function () { setStepKey(next); }}>
            Continue
          </Button>
        </>
      );
    }
    if (stepKey === "policies") {
      // Say WHY the button is off. A disabled control with no explanation is the
      // most common way a flow like this stalls.
      const remaining = selectedPolicies.filter(function (p) { return !approved[p.id]; }).length;
      return (
        <>
          {remaining ? (
            <span className="ir-foot-hint">
              {remaining === 1 ? "1 policy still to approve." : remaining + " policies still to approve."}
            </span>
          ) : null}
          {backButton()}
          {next ? (
            <Button type="primary" disabled={!policiesReady} onClick={function () { setStepKey(next); }}>
              Continue
            </Button>
          ) : sendButton()}
        </>
      );
    }
    return (
      <>
        {!estimatesReady ? (
          <span className="ir-foot-hint">
            {estimatesOutstanding === 1
              ? "1 policy still needs the details of " + meta.submissionNounWithArticle + " to track."
              : estimatesOutstanding + " policies still need the details of "
                + meta.submissionNounWithArticle + " to track."}
          </span>
        ) : null}
        {backButton()}
        {sendButton()}
      </>
    );
  }

  const currentIndex = Math.max(0, steps.findIndex(function (s) { return s.key === stepKey; }));

  return (
    <>
      <Drawer
        className="ir-drawer"
        open={open}
        onClose={onClose}
        width={560}
        zIndex={zIndex}
        title={
          seededPatient && initialPatient
            ? "New request — " + [initialPatient.first_name, initialPatient.last_name]
                .filter(Boolean).join(" ")
            : "New insurance request"
        }
        destroyOnClose={false}
      >
        <div className="ir-drawer-steps">
          <Steps
            size="small"
            current={currentIndex}
            items={steps.map(function (s) { return { title: s.title }; })}
          />
        </div>

        <div className="ir-drawer-body">
          {stepKey === "type" ? (
            <RequestTypeStep requestType={requestType} onRequestType={setRequestType} />
          ) : stepKey === "patient" ? (
            <PatientStep
              source={patientSource}
              onSource={handleSource}
              patient={patient}
              onSelect={handleSelectPatient}
              officeId={selectedOfficeId()}
              claimForm={claimForm}
              onClaimFormRead={handleClaimFormRead}
              onClaimFormReplace={handleClaimFormReplace}
            />
          ) : stepKey === "policies" ? (
            <PolicySelectStep
              patient={patient}
              policies={allPolicies}
              loadingPolicies={loadingPolicies || isFetchingPolicies}
              selectedIds={selectedIds}
              onToggle={toggle}
              drafts={drafts}
              approved={approved}
              previews={reviewPreviews}
              onDraftChange={handleDraftChange}
              onApproved={handlePolicyApproved}
              onSavePending={savePendingPolicy}
              focusPolicyId={focusPolicyId}
              // Only when the form goes with the request — a breakdown uses it to
              // find the patient and policy, and sends nothing else.
              claimFormSummary={usingClaimForm && needsSubmissions(requestType)
                ? describeClaimForm(claimForm) || claimForm.file_name
                : null}
              urgent={urgent}
              onUrgent={setUrgent}
              onAddPolicy={function () { setAddingPolicy(true); }}
              addingPolicy={addingPolicy}
              renderAddPolicyForm={renderAddPolicyForm}
              footer={
                <OfficeCdaPrompt
                  patientId={patient && patient.id}
                  value={cdaItrans}
                  onSaved={setCdaItrans}
                />
              }
            />
          ) : (
            // No intro line here on purpose: the checklist's own progress header
            // states the ask, and the dropzone states the upload shortcut.
            <EstimateChecklist
              patientId={patient && patient.id}
              policies={selectedPolicies}
              noun={meta.submissionNoun}
              nounPlural={meta.submissionNounPlural}
              value={estimates}
              saved={savedEstimates}
              providerOptions={providerOptions}
              cdaItrans={cdaItrans}
              onChange={handleEstimatesChange}
              onSave={function (policyId) {
                setSavedEstimates(function (prev) {
                  return Object.assign({}, prev, { [policyId]: true });
                });
              }}
            />
          )}
        </div>

        <div className="ir-drawer-foot">{footer()}</div>
      </Drawer>
    </>
  );
}
